import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, UserPlus, Search,
  MoreHorizontal, Key, Smartphone, Edit2, Trash2,
  X, Loader2, RefreshCw, Lock, Unlock, Eye, EyeOff,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { adminUsersApi, UserDto, CreateUserPayload, UpdateUserPayload } from '../services/api';

// ─── Constants ────────────────────────────────────────────────────────────────

const AVAILABLE_ROLES = [
  { code: 'ROLE_SYS_ADMIN',      label: 'Super Admin',       color: 'text-rose-600 dark:text-rose-400',     bg: 'bg-rose-500/10',   border: 'border-rose-500/20' },
  { code: 'ROLE_KIEM_TOAN_VIEN', label: 'Kiểm toán viên',    color: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-500/10', border: 'border-indigo-500/20' },
  { code: 'ROLE_TRUONG_DOAN',    label: 'Trưởng đoàn',       color: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-500/10', border: 'border-violet-500/20' },
  { code: 'ROLE_CB_DONVI',       label: 'Cán bộ đơn vị',     color: 'text-cyan-600 dark:text-cyan-400',    bg: 'bg-cyan-500/10',   border: 'border-cyan-500/20' },
  { code: 'ROLE_LANH_DAO_DONVI', label: 'Lãnh đạo đơn vị',  color: 'text-amber-600 dark:text-amber-400',  bg: 'bg-amber-500/10',  border: 'border-amber-500/20' },
  { code: 'ROLE_READONLY_C12',   label: 'C12 Read-only',     color: 'text-slate-600 dark:text-slate-400',  bg: 'bg-slate-500/10',  border: 'border-slate-500/20' },
];

const roleConfig = (code: string) =>
  AVAILABLE_ROLES.find(r => r.code === code) ?? {
    code, label: code, color: 'text-slate-500', bg: 'bg-slate-100', border: 'border-slate-200',
  };

const STATUS_CONFIG: Record<string, { label: string; dot: string }> = {
  ACTIVE:   { label: 'Hoạt động', dot: 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)] animate-pulse' },
  INACTIVE: { label: 'Vô hiệu',   dot: 'bg-slate-400 dark:bg-slate-600' },
  LOCKED:   { label: 'Bị khóa',   dot: 'bg-rose-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]' },
};

// ─── User Form Modal ──────────────────────────────────────────────────────────

interface UserFormProps {
  initial?: UserDto | null;
  onSave: (payload: CreateUserPayload | UpdateUserPayload) => Promise<void>;
  onClose: () => void;
}

function UserFormModal({ initial, onSave, onClose }: UserFormProps) {
  const isEdit = !!initial;
  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [form, setForm] = useState({
    username:   initial?.username   ?? '',
    email:      initial?.email      ?? '',
    fullName:   initial?.fullName   ?? '',
    password:   '',
    department: initial?.department ?? '',
    title:      initial?.title      ?? '',
    mfaEnabled: initial?.mfaEnabled ?? false,
    roles:      initial?.roles      ?? [] as string[],
  });

  const toggleRole = (code: string) =>
    setForm(f => ({
      ...f,
      roles: f.roles.includes(code) ? f.roles.filter(r => r !== code) : [...f.roles, code],
    }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.fullName.trim())   { toast.error('Vui lòng nhập họ tên'); return; }
    if (!form.email.trim())      { toast.error('Vui lòng nhập email'); return; }
    if (form.roles.length === 0) { toast.error('Vui lòng chọn ít nhất 1 vai trò'); return; }
    if (!isEdit && form.password.length < 8) { toast.error('Mật khẩu phải có ít nhất 8 ký tự'); return; }

    setSaving(true);
    try {
      const payload: any = {
        email:      form.email,
        fullName:   form.fullName,
        roles:      form.roles,
        department: form.department || undefined,
        title:      form.title      || undefined,
        mfaEnabled: form.mfaEnabled,
      };
      if (!isEdit) {
        payload.username = form.username;
        payload.password = form.password;
      } else if (form.password) {
        payload.password = form.password;
      }
      await onSave(payload);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="w-full max-w-xl bg-white dark:bg-[#0f0f1a] rounded-[2rem] border border-slate-200 dark:border-white/10 shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-slate-100 dark:border-white/5 bg-slate-50/50 dark:bg-white/[0.02]">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-500/10 rounded-xl">
              {isEdit
                ? <Edit2 size={18} className="text-indigo-600 dark:text-indigo-400" />
                : <UserPlus size={18} className="text-indigo-600 dark:text-indigo-400" />}
            </div>
            <div>
              <h3 className="text-[15px] font-black text-slate-900 dark:text-white tracking-tight">
                {isEdit ? 'Chỉnh sửa tài khoản' : 'Thêm tài khoản mới'}
              </h3>
              {isEdit && <p className="text-[10px] text-slate-500 tracking-wider mt-0.5 font-mono">{initial?.username}</p>}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-white/5 transition-colors text-slate-400">
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-8 space-y-5 max-h-[75vh] overflow-y-auto">
          {/* Username — only on create */}
          {!isEdit && (
            <div>
              <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 tracking-[0.15em] block mb-2">TÊN ĐĂNG NHẬP *</label>
              <input
                className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.03] text-sm font-medium text-slate-900 dark:text-white outline-none focus:border-indigo-500/60 focus:ring-2 focus:ring-indigo-500/10 transition-all"
                placeholder="Ví dụ: nguyenvana"
                value={form.username}
                onChange={e => setForm(f => ({ ...f, username: e.target.value.toLowerCase().replace(/\s/g, '') }))}
                required
              />
            </div>
          )}

          {/* Full name + Email */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 tracking-[0.15em] block mb-2">HỌ VÀ TÊN *</label>
              <input
                className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.03] text-sm font-medium text-slate-900 dark:text-white outline-none focus:border-indigo-500/60 focus:ring-2 focus:ring-indigo-500/10 transition-all"
                placeholder="Nguyễn Văn A"
                value={form.fullName}
                onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 tracking-[0.15em] block mb-2">EMAIL *</label>
              <input
                type="email"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.03] text-sm font-medium text-slate-900 dark:text-white outline-none focus:border-indigo-500/60 focus:ring-2 focus:ring-indigo-500/10 transition-all"
                placeholder="email@domain.gov.vn"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                required
              />
            </div>
          </div>

          {/* Department + Title */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 tracking-[0.15em] block mb-2">CƠ QUAN / ĐƠN VỊ</label>
              <input
                className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.03] text-sm font-medium text-slate-900 dark:text-white outline-none focus:border-indigo-500/60 focus:ring-2 focus:ring-indigo-500/10 transition-all"
                placeholder="Bộ Công an (C12)"
                value={form.department}
                onChange={e => setForm(f => ({ ...f, department: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 tracking-[0.15em] block mb-2">CHỨC VỤ</label>
              <input
                className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.03] text-sm font-medium text-slate-900 dark:text-white outline-none focus:border-indigo-500/60 focus:ring-2 focus:ring-indigo-500/10 transition-all"
                placeholder="Chuyên viên"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 tracking-[0.15em] block mb-2">
              MẬT KHẨU {isEdit ? <span className="font-medium normal-case">(để trống nếu không đổi)</span> : '*'}
            </label>
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                className="w-full px-4 py-3 pr-11 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.03] text-sm font-medium text-slate-900 dark:text-white outline-none focus:border-indigo-500/60 focus:ring-2 focus:ring-indigo-500/10 transition-all"
                placeholder={isEdit ? '••••••••' : 'Tối thiểu 8 ký tự'}
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                minLength={isEdit ? undefined : 8}
              />
              <button
                type="button"
                onClick={() => setShowPwd(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors p-1"
              >
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {/* Roles */}
          <div>
            <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 tracking-[0.15em] block mb-3">VAI TRÒ *</label>
            <div className="grid grid-cols-2 gap-2">
              {AVAILABLE_ROLES.map(r => (
                <button
                  key={r.code}
                  type="button"
                  onClick={() => toggleRole(r.code)}
                  className={`px-3 py-2.5 rounded-xl border text-[10px] font-black tracking-tight text-left transition-all ${
                    form.roles.includes(r.code)
                      ? `${r.bg} ${r.border} ${r.color} shadow-sm`
                      : 'border-slate-200 dark:border-white/5 text-slate-500 hover:bg-slate-50 dark:hover:bg-white/5'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {/* MFA toggle */}
          <div className="flex items-center justify-between px-4 py-3 rounded-xl border border-slate-200 dark:border-white/5 bg-slate-50/50 dark:bg-white/[0.02]">
            <div className="flex items-center gap-3">
              <Smartphone size={16} className="text-slate-500" />
              <div>
                <p className="text-[12px] font-black text-slate-700 dark:text-slate-300">Xác thực hai yếu tố (MFA)</p>
                <p className="text-[9px] text-slate-400 mt-0.5">OTP / Authenticator App</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, mfaEnabled: !f.mfaEnabled }))}
              className={`w-11 h-6 rounded-full transition-all relative ${form.mfaEnabled ? 'bg-emerald-500' : 'bg-slate-200 dark:bg-slate-700'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${form.mfaEnabled ? 'translate-x-5' : ''}`} />
            </button>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 rounded-xl border border-slate-200 dark:border-white/10 text-sm font-black text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-white/5 transition-all"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-3 rounded-xl bg-gradient-to-r from-indigo-500 to-indigo-600 text-white text-sm font-black shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {isEdit ? 'Lưu thay đổi' : 'Tạo tài khoản'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// ─── Delete Confirmation ──────────────────────────────────────────────────────

function DeleteModal({ user, onConfirm, onClose }: { user: UserDto; onConfirm: () => Promise<void>; onClose: () => void }) {
  const [loading, setLoading] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="w-full max-w-sm bg-white dark:bg-[#0f0f1a] rounded-[2rem] border border-slate-200 dark:border-white/10 shadow-2xl p-8 text-center"
      >
        <div className="w-14 h-14 rounded-2xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center mx-auto mb-5">
          <Trash2 size={24} className="text-rose-500" />
        </div>
        <h3 className="text-lg font-black text-slate-900 dark:text-white mb-2">Vô hiệu hóa tài khoản?</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">
          Tài khoản <strong className="text-slate-700 dark:text-slate-300">{user.fullName}</strong> sẽ chuyển sang trạng thái <strong>INACTIVE</strong>.
        </p>
        <p className="text-[10px] text-slate-400 mb-6">Dữ liệu không bị xóa vĩnh viễn, có thể kích hoạt lại sau.</p>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl border border-slate-200 dark:border-white/10 text-sm font-black text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-white/5 transition-all">
            Hủy
          </button>
          <button
            onClick={async () => { setLoading(true); await onConfirm(); setLoading(false); }}
            disabled={loading}
            className="flex-1 py-3 rounded-xl bg-rose-600 text-white text-sm font-black hover:bg-rose-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            Xác nhận
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function UsersPage() {
  const [users, setUsers]           = useState<UserDto[]>([]);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);
  const [loading, setLoading]       = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter,   setRoleFilter]   = useState('');
  const [showCreate,  setShowCreate]  = useState(false);
  const [editUser,    setEditUser]    = useState<UserDto | null>(null);
  const [deleteUser,  setDeleteUser]  = useState<UserDto | null>(null);

  const LIMIT = 15;

  // Debounce search 350 ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminUsersApi.list({
        search: debouncedSearch || undefined,
        status: statusFilter    || undefined,
        role:   roleFilter      || undefined,
        page,
        limit: LIMIT,
      });
      setUsers(res.data);
      setTotal(res.meta.total);
    } catch {
      toast.error('Không thể tải danh sách người dùng');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, statusFilter, roleFilter, page]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [debouncedSearch, statusFilter, roleFilter]);

  const handleCreate = async (payload: CreateUserPayload | UpdateUserPayload) => {
    await adminUsersApi.create(payload as CreateUserPayload);
    toast.success('Đã tạo tài khoản thành công');
    await fetchUsers();
  };

  const handleUpdate = async (payload: CreateUserPayload | UpdateUserPayload) => {
    if (!editUser) return;
    await adminUsersApi.update(editUser.id, payload as UpdateUserPayload);
    toast.success('Đã cập nhật tài khoản');
    await fetchUsers();
  };

  const handleToggleStatus = async (user: UserDto) => {
    try {
      await adminUsersApi.toggleStatus(user.id);
      const next = user.status === 'ACTIVE' ? 'Vô hiệu hóa' : 'Kích hoạt';
      toast.success(`Tài khoản đã ${next.toLowerCase()}`);
      await fetchUsers();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? 'Thao tác thất bại');
    }
  };

  const handleUnlock = async (user: UserDto) => {
    try {
      await adminUsersApi.unlock(user.id);
      toast.success('Tài khoản đã được mở khóa');
      await fetchUsers();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? 'Thao tác thất bại');
    }
  };

  const handleDelete = async () => {
    if (!deleteUser) return;
    try {
      await adminUsersApi.delete(deleteUser.id);
      toast.success('Đã vô hiệu hóa tài khoản');
      setDeleteUser(null);
      await fetchUsers();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? 'Thao tác thất bại');
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="space-y-8 animate-in fade-in duration-500 relative pb-10">

      {/* ── HEADER ── */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-3 leading-tight">
            <Users className="text-indigo-600 dark:text-indigo-400" size={32} />
            Quản trị Người dùng
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mt-2 max-w-2xl font-medium tracking-tight text-xs leading-relaxed">
            Quản lý tài khoản cán bộ, kiểm toán viên và API Clients.{' '}
            Tổng <span className="font-black text-indigo-600 dark:text-indigo-400">{total}</span> tài khoản.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={fetchUsers}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-400 text-[10px] font-black hover:bg-slate-50 dark:hover:bg-white/5 transition-all"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Làm mới
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-indigo-500 to-indigo-600 text-white text-[10px] font-black tracking-[0.15em] shadow-[0_8px_24px_rgba(99,102,241,0.3)] hover:shadow-[0_12px_32px_rgba(99,102,241,0.5)] transition-all transform hover:-translate-y-0.5 active:scale-95"
          >
            <UserPlus size={16} /> Thêm tài khoản
          </button>
        </div>
      </div>

      {/* ── FILTERS ── */}
      <div className="glass-panel p-4 flex flex-col md:flex-row items-center gap-4 border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.02] shadow-sm">
        {/* Status tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1 md:pb-0 no-scrollbar">
          {[
            { id: '', label: 'Tất cả' },
            { id: 'ACTIVE',   label: 'Hoạt động' },
            { id: 'INACTIVE', label: 'Vô hiệu' },
            { id: 'LOCKED',   label: 'Bị khóa' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setStatusFilter(tab.id)}
              className={`px-4 py-2 rounded-xl text-[10px] font-black tracking-tight transition-all border shadow-sm whitespace-nowrap ${
                statusFilter === tab.id
                  ? 'bg-indigo-600 text-white border-indigo-400 shadow-indigo-500/20'
                  : 'bg-slate-50 dark:bg-white/5 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-white/5 hover:bg-slate-100 dark:hover:bg-white/10'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Role filter */}
        <select
          value={roleFilter}
          onChange={e => setRoleFilter(e.target.value)}
          className="px-4 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.03] text-[11px] font-black text-slate-600 dark:text-slate-400 outline-none focus:border-indigo-500/50 transition-all cursor-pointer"
        >
          <option value="">Tất cả vai trò</option>
          {AVAILABLE_ROLES.map(r => (
            <option key={r.code} value={r.code}>{r.label}</option>
          ))}
        </select>

        {/* Search */}
        <div className="flex items-center gap-2 bg-slate-50 dark:bg-black/20 border border-slate-200 dark:border-white/5 rounded-xl px-4 py-2.5 flex-1 focus-within:border-indigo-500/50 transition-all shadow-inner">
          <Search size={14} className="text-slate-400 shrink-0" />
          <input
            type="text"
            placeholder="Tra cứu tên, email, cơ quan..."
            className="bg-transparent text-xs outline-none w-full text-slate-900 dark:text-white font-medium"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="text-slate-400 hover:text-slate-600 transition-colors">
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      {/* ── DATA TABLE ── */}
      <div className="glass-panel overflow-hidden border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.01] shadow-sm rounded-[2.5rem]">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-100 dark:border-white/5 bg-slate-50/50 dark:bg-white/[0.02] text-[9px] font-black text-slate-400 dark:text-slate-500 tracking-[0.2em]">
                <th className="px-8 py-5">Cán bộ / Tài khoản</th>
                <th className="px-8 py-5">Cơ quan</th>
                <th className="px-8 py-5 text-center">Vai trò</th>
                <th className="px-8 py-5 text-center whitespace-nowrap">Bảo mật</th>
                <th className="px-8 py-5 text-center">Trạng thái</th>
                <th className="px-8 py-5 text-right whitespace-nowrap">Lần cuối đăng nhập</th>
                <th className="px-8 py-5 text-center"><MoreHorizontal size={16} className="mx-auto opacity-40" /></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-white/5">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-8 py-16 text-center">
                    <Loader2 size={28} className="animate-spin text-indigo-500 mx-auto mb-3" />
                    <p className="text-[11px] text-slate-400 font-medium">Đang tải dữ liệu...</p>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-8 py-16 text-center">
                    <Users size={36} className="text-slate-200 dark:text-slate-700 mx-auto mb-3" />
                    <p className="text-[12px] text-slate-400 font-medium">Không tìm thấy tài khoản nào</p>
                    {(debouncedSearch || statusFilter || roleFilter) && (
                      <button
                        onClick={() => { setSearchTerm(''); setStatusFilter(''); setRoleFilter(''); }}
                        className="mt-3 text-[10px] text-indigo-500 hover:underline font-black"
                      >
                        Xóa bộ lọc
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                <AnimatePresence mode="popLayout">
                  {users.map((user, idx) => {
                    const sc = STATUS_CONFIG[user.status] ?? STATUS_CONFIG['INACTIVE'];
                    return (
                      <motion.tr
                        layout
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.03 }}
                        key={user.id}
                        className="group hover:bg-slate-50 dark:hover:bg-white/[0.03] transition-all"
                      >
                        {/* Avatar + Name */}
                        <td className="px-8 py-5">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 border border-white/20 flex items-center justify-center text-[11px] font-black text-white shadow-md shrink-0">
                              {user.fullName.split(' ').map(n => n[0]).slice(-2).join('')}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[13px] font-black text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors truncate tracking-tight">
                                {user.fullName}
                              </p>
                              <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 truncate mt-0.5 tracking-tighter">
                                {user.email}
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Department */}
                        <td className="px-8 py-5">
                          <p className="text-[11px] font-black text-slate-600 dark:text-slate-400 tracking-tight line-clamp-1 max-w-[160px]">
                            {user.department ?? <span className="text-slate-300 dark:text-slate-600 font-normal">—</span>}
                          </p>
                        </td>

                        {/* Roles */}
                        <td className="px-8 py-5 text-center">
                          <div className="flex flex-wrap gap-1 justify-center">
                            {user.roles.slice(0, 2).map(r => {
                              const cfg = roleConfig(r);
                              return (
                                <span
                                  key={r}
                                  className={`inline-block px-2.5 py-1 text-[9px] font-black rounded-full border tracking-tighter shadow-sm ${cfg.bg} ${cfg.color} ${cfg.border}`}
                                >
                                  {cfg.label}
                                </span>
                              );
                            })}
                            {user.roles.length > 2 && (
                              <span className="text-[9px] font-black text-slate-400 px-2">+{user.roles.length - 2}</span>
                            )}
                          </div>
                        </td>

                        {/* MFA */}
                        <td className="px-8 py-5 text-center">
                          {user.mfaEnabled ? (
                            <div className="flex items-center justify-center gap-1.5 text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-3 py-1.5 rounded-xl w-max mx-auto border border-emerald-500/20 shadow-sm">
                              <Smartphone size={11} /> <span className="text-[9px] font-black tracking-widest">MFA</span>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-1.5 text-slate-400 bg-slate-100 dark:bg-black/40 px-3 py-1.5 rounded-xl w-max mx-auto border border-slate-200 dark:border-white/5">
                              <Key size={11} /> <span className="text-[9px] font-black tracking-widest">Password</span>
                            </div>
                          )}
                        </td>

                        {/* Status */}
                        <td className="px-8 py-5 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${sc.dot}`} />
                            <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 tracking-widest">{sc.label}</span>
                          </div>
                        </td>

                        {/* Last login */}
                        <td className="px-8 py-5 text-right">
                          <p className="text-[10px] text-slate-400 dark:text-slate-500 font-medium whitespace-nowrap">
                            {user.lastLoginAt
                              ? new Date(user.lastLoginAt).toLocaleDateString('vi-VN', {
                                  day: '2-digit', month: '2-digit', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit',
                                })
                              : 'Chưa đăng nhập'
                            }
                          </p>
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-5 text-center">
                          <div className="flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-all">
                            {/* Edit */}
                            <button
                              onClick={() => setEditUser(user)}
                              className="p-2 bg-slate-100 dark:bg-white/5 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-600 hover:text-white rounded-xl transition-all shadow-sm active:scale-90"
                              title="Chỉnh sửa"
                            >
                              <Edit2 size={13} />
                            </button>

                            {/* Lock / Unlock */}
                            {user.status === 'LOCKED' ? (
                              <button
                                onClick={() => handleUnlock(user)}
                                className="p-2 bg-slate-100 dark:bg-white/5 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-600 hover:text-white rounded-xl transition-all shadow-sm active:scale-90"
                                title="Mở khóa tài khoản"
                              >
                                <Unlock size={13} />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleToggleStatus(user)}
                                className={`p-2 bg-slate-100 dark:bg-white/5 rounded-xl transition-all shadow-sm active:scale-90 ${
                                  user.status === 'ACTIVE'
                                    ? 'text-amber-600 hover:bg-amber-500 hover:text-white'
                                    : 'text-emerald-600 hover:bg-emerald-500 hover:text-white'
                                }`}
                                title={user.status === 'ACTIVE' ? 'Vô hiệu hóa' : 'Kích hoạt lại'}
                              >
                                {user.status === 'ACTIVE' ? <Lock size={13} /> : <Unlock size={13} />}
                              </button>
                            )}

                            {/* Delete (soft) */}
                            <button
                              onClick={() => setDeleteUser(user)}
                              className="p-2 bg-slate-100 dark:bg-white/5 text-rose-600 dark:text-rose-400 hover:bg-rose-600 hover:text-white rounded-xl transition-all shadow-sm active:scale-90"
                              title="Vô hiệu hóa tài khoản"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-8 py-5 border-t border-slate-100 dark:border-white/5 bg-slate-50/30 dark:bg-white/[0.01]">
            <p className="text-[10px] font-black text-slate-400 tracking-wide">
              Hiển thị {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)} / {total} tài khoản
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-xl border border-slate-200 dark:border-white/5 text-slate-500 disabled:opacity-30 hover:bg-slate-100 dark:hover:bg-white/5 transition-all"
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                return (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-9 h-9 rounded-xl text-[11px] font-black transition-all border ${
                      p === page
                        ? 'bg-indigo-600 text-white border-indigo-400 shadow-md shadow-indigo-500/20'
                        : 'border-slate-200 dark:border-white/5 text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5'
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-xl border border-slate-200 dark:border-white/5 text-slate-500 disabled:opacity-30 hover:bg-slate-100 dark:hover:bg-white/5 transition-all"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      <AnimatePresence>
        {showCreate && (
          <UserFormModal
            onSave={handleCreate}
            onClose={() => setShowCreate(false)}
          />
        )}
        {editUser && (
          <UserFormModal
            initial={editUser}
            onSave={handleUpdate}
            onClose={() => setEditUser(null)}
          />
        )}
        {deleteUser && (
          <DeleteModal
            user={deleteUser}
            onConfirm={handleDelete}
            onClose={() => setDeleteUser(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
